import { useState, useEffect, useCallback, useRef } from "react";
import api from "../api";
import { useTheme } from "../components/Theme";

const ORANGE = "#FF6B2B";

const CORES = [
  "#6366f1","#f59e0b","#10b981","#3b82f6","#e879f9","#ef4444",
  "#8b5cf6","#ec4899","#14b8a6","#f97316","#84cc16","#06b6d4",
];

const STATUS_LEAD = [
  { id: "novo",             label: "Novo lead",            color: "#6366f1" },
  { id: "tentativa",        label: "Tentativa de contato", color: "#f59e0b" },
  { id: "contato_feito",    label: "Contato feito",        color: "#3b82f6" },
  { id: "qualificado",      label: "Qualificado",          color: "#8b5cf6" },
  { id: "reuniao_agendada", label: "Reunião agendada",     color: "#e879f9" },
  { id: "proposta_enviada", label: "Proposta enviada",     color: "#f97316" },
  { id: "convertido",       label: "Convertido",           color: "#10b981" },
  { id: "perdido",          label: "Perdido",              color: "#ef4444" },
  { id: "inativo",          label: "Inativo",              color: "#8E8E93" },
];

const ORIGENS = ["Instagram","Google","Google Ads","LinkedIn","WhatsApp","Facebook","Site","monday Form","Indicação","Outro"];

function fmtData(d) {
  if (!d) return "—";
  const data = new Date(d);
  const hoje = new Date();
  const diff = Math.floor((hoje - data) / (1000*60*60*24));
  if (diff === 0) return "Hoje";
  if (diff === 1) return "Ontem";
  if (diff < 7)  return `${diff}d atrás`;
  return data.toLocaleDateString("pt-BR", { day:"2-digit", month:"short" });
}

// ── STATUS BADGE CLICÁVEL ────────────────────────────────
function StatusBadge({ statusLead, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const st = STATUS_LEAD.find(s=>s.id===statusLead) || STATUS_LEAD[0];
  useEffect(() => {
    const h = e => { if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} style={{ position:"relative", display:"inline-block" }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ background:st.color, borderRadius:6, padding:"4px 14px", fontSize:12, fontWeight:600, color:"#fff", cursor:"pointer", whiteSpace:"nowrap" }}>
        {st.label}
      </div>
      {open && (
        <div style={{ position:"absolute", top:"110%", left:0, zIndex:500, background:"#2C2C2F", border:"1px solid #3D3D42", borderRadius:12, padding:6, minWidth:200, boxShadow:"0 12px 40px rgba(0,0,0,.5)" }}>
          {STATUS_LEAD.map(s=>(
            <div key={s.id} onClick={()=>{ onChange(s.id); setOpen(false); }}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 10px", borderRadius:8, cursor:"pointer" }}
              onMouseEnter={ev=>ev.currentTarget.style.background=s.color+"18"}
              onMouseLeave={ev=>ev.currentTarget.style.background="transparent"}>
              <div style={{ width:10, height:10, borderRadius:3, background:s.color }}/>
              <span style={{ fontSize:13, color:"#F2F2F7", fontWeight:statusLead===s.id?600:400 }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ORIGEM SELECT INLINE ─────────────────────────────────
function OrigemSelect({ value, onChange }) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = e => { if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} style={{ position:"relative" }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ background:value?theme.bgInput:"transparent", borderRadius:6, padding:"3px 10px", fontSize:12, color:value?theme.textSub:theme.textMuted, cursor:"pointer", whiteSpace:"nowrap", border:`1px solid ${open?ORANGE:theme.border}` }}>
        {value||"Selecionar..."}
      </div>
      {open && (
        <div style={{ position:"absolute", top:"110%", left:0, zIndex:500, background:"#2C2C2F", border:"1px solid #3D3D42", borderRadius:12, padding:6, minWidth:180, boxShadow:"0 12px 40px rgba(0,0,0,.5)", maxHeight:260, overflowY:"auto" }}>
          {ORIGENS.map(o=>(
            <div key={o} onClick={()=>{ onChange(o); setOpen(false); }}
              style={{ padding:"7px 10px", borderRadius:8, cursor:"pointer", fontSize:13, color:"#F2F2F7", fontWeight:value===o?600:400 }}
              onMouseEnter={ev=>ev.currentTarget.style.background=ORANGE+"18"}
              onMouseLeave={ev=>ev.currentTarget.style.background="transparent"}>
              {o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── INLINE EDIT ──────────────────────────────────────────
function IE({ value, onChange, placeholder="—", style:s={}, autoFocus=false }) {
  const { theme } = useTheme();
  const [editing, setEditing] = useState(autoFocus);
  const [val, setVal] = useState(value||"");
  const ref = useRef();
  useEffect(()=>{ setVal(value||""); },[value]);
  useEffect(()=>{ if(editing&&ref.current) ref.current.focus(); },[editing]);
  const commit=()=>{ setEditing(false); if(String(val)!==String(value||"")) onChange(val); };
  if(editing) return (
    <input ref={ref} value={val} onChange={e=>setVal(e.target.value)}
      onBlur={commit} onKeyDown={e=>{ if(e.key==="Enter")commit(); if(e.key==="Escape"){setVal(value||"");setEditing(false);} }}
      style={{ background:"transparent", border:"none", borderBottom:`1.5px solid ${ORANGE}`, outline:"none", color:theme.text, fontSize:13, width:"100%", padding:"2px 0", fontFamily:"'Inter',sans-serif", ...s }}/>
  );
  return (
    <div onClick={()=>setEditing(true)} style={{ cursor:"text", color:value?theme.text:theme.textMuted, fontSize:13, padding:"2px 4px", borderRadius:6, minWidth:20, ...s }}
      onMouseEnter={e=>e.currentTarget.style.background="rgba(255,107,43,.08)"}
      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
      {value||<span style={{ color:theme.textMuted, fontSize:12 }}>{placeholder}</span>}
    </div>
  );
}

// ── LINHA NOVA ESTILO MONDAY ─────────────────────────────
// Linha completamente editável logo após o cabeçalho do grupo
function LinhaNovaMonday({ grupoId, grupoLabel, grupos, onSave, onCancel }) {
  const { theme } = useTheme();
  const [form, setForm] = useState({
    nome:"", empresa:"", cargo:"", email:"", telefone:"",
    origem:"Instagram", statusLead:"novo",
    grupoLeadId: grupoId||null, grupoLeadLabel: grupoLabel||null,
    comentarios:"",
  });
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const nomeRef = useRef();

  useEffect(()=>{ nomeRef.current?.focus(); },[]);

  const salvar=async()=>{
    if(!form.nome.trim()) return nomeRef.current?.focus();
    await onSave(form);
  };

  const handleKey=(e)=>{
    if(e.key==="Escape") onCancel();
  };

  const inp={ background:"transparent", border:"none", borderBottom:`1px solid ${theme.border}`, outline:"none", color:theme.text, fontSize:13, width:"100%", padding:"2px 4px", fontFamily:"'Inter',sans-serif", colorScheme:theme.name };
  const inpFocus={ borderBottomColor:ORANGE };

  return (
    <tr style={{ background:theme.bgInput + "CC" }}>
      <td style={{ padding:"8px 10px", width:36 }}>
        <input type="checkbox" disabled/>
      </td>

      {/* Lead / Empresa */}
      <td style={{ padding:"4px 10px", width:240, borderRight:`1px solid ${theme.border}` }}>
        <input ref={nomeRef} value={form.nome} onChange={e=>set("nome",e.target.value)}
          onKeyDown={handleKey} placeholder="Nome do lead *"
          style={{ ...inp, fontWeight:600 }}
          onFocus={e=>e.target.style.borderBottomColor=ORANGE}
          onBlur={e=>e.target.style.borderBottomColor=theme.border}/>
        <input value={form.empresa} onChange={e=>set("empresa",e.target.value)}
          onKeyDown={handleKey} placeholder="Empresa"
          style={{ ...inp, fontSize:11, marginTop:2, color:theme.textMuted }}
          onFocus={e=>e.target.style.borderBottomColor=ORANGE}
          onBlur={e=>e.target.style.borderBottomColor=theme.border}/>
      </td>

      {/* Status */}
      <td style={{ padding:"4px 10px", width:190, borderRight:`1px solid ${theme.border}`, textAlign:"center" }}>
        <StatusBadge statusLead={form.statusLead} onChange={v=>set("statusLead",v)}/>
      </td>

      {/* Cargo */}
      <td style={{ padding:"4px 10px", width:120, borderRight:`1px solid ${theme.border}` }}>
        <input value={form.cargo} onChange={e=>set("cargo",e.target.value)}
          onKeyDown={handleKey} placeholder="Cargo"
          style={inp}
          onFocus={e=>e.target.style.borderBottomColor=ORANGE}
          onBlur={e=>e.target.style.borderBottomColor=theme.border}/>
      </td>

      {/* Email */}
      <td style={{ padding:"4px 10px", width:170, borderRight:`1px solid ${theme.border}` }}>
        <input type="email" value={form.email} onChange={e=>set("email",e.target.value)}
          onKeyDown={handleKey} placeholder="email@..."
          style={{ ...inp, color:"#3b82f6" }}
          onFocus={e=>e.target.style.borderBottomColor=ORANGE}
          onBlur={e=>e.target.style.borderBottomColor=theme.border}/>
      </td>

      {/* Telefone */}
      <td style={{ padding:"4px 10px", width:130, borderRight:`1px solid ${theme.border}` }}>
        <input value={form.telefone} onChange={e=>set("telefone",e.target.value)}
          onKeyDown={handleKey} placeholder="(15) 99999-9999"
          style={{ ...inp, color:"#3b82f6" }}
          onFocus={e=>e.target.style.borderBottomColor=ORANGE}
          onBlur={e=>e.target.style.borderBottomColor=theme.border}/>
      </td>

      {/* Fonte */}
      <td style={{ padding:"4px 10px", width:130, borderRight:`1px solid ${theme.border}` }}>
        <OrigemSelect value={form.origem} onChange={v=>set("origem",v)}/>
      </td>

      {/* Última interação */}
      <td style={{ padding:"4px 10px", width:110, borderRight:`1px solid ${theme.border}` }}>
        <span style={{ fontSize:11, color:theme.textMuted }}>—</span>
      </td>

      {/* Comentários */}
      <td style={{ padding:"4px 10px", width:200, borderRight:`1px solid ${theme.border}` }}>
        <input value={form.comentarios} onChange={e=>set("comentarios",e.target.value)}
          onKeyDown={handleKey} placeholder="Comentário..."
          style={inp}
          onFocus={e=>e.target.style.borderBottomColor=ORANGE}
          onBlur={e=>e.target.style.borderBottomColor=theme.border}/>
      </td>

      {/* Ações */}
      <td style={{ padding:"4px 10px", width:52, textAlign:"center" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
          <button onClick={salvar} style={{ background:ORANGE, border:"none", borderRadius:6, padding:"3px 8px", color:"#fff", fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>✓ Salvar</button>
          <button onClick={onCancel} style={{ background:"none", border:`1px solid ${theme.border}`, borderRadius:6, padding:"3px 8px", color:theme.textMuted, fontSize:11, cursor:"pointer" }}>✕</button>
        </div>
      </td>
    </tr>
  );
}

// ── MODAL GRUPO ──────────────────────────────────────────
function ModalGrupo({ grupo, onClose, onSave, onDelete }) {
  const { theme } = useTheme();
  const isNew = !grupo?._id;
  const [label, setLabel] = useState(grupo?.label||"");
  const [color, setColor] = useState(grupo?.color||CORES[0]);
  const [load, setLoad]   = useState(false);
  const inp = { width:"100%", boxSizing:"border-box", background:theme.bgInput, border:`1.5px solid ${theme.border}`, borderRadius:10, padding:"10px 14px", color:theme.text, fontSize:14, outline:"none", fontFamily:"'Inter',sans-serif" };
  const salvar=async()=>{
    if(!label.trim()) return;
    setLoad(true);
    try{ await onSave({...grupo,label,color}); onClose(); }catch(e){ alert(e.response?.data?.erro||"Erro"); }finally{ setLoad(false); }
  };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.7)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:16, padding:28, width:"100%", maxWidth:380, boxShadow:"0 24px 80px rgba(0,0,0,.6)" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <h3 style={{ margin:0, fontSize:17, fontWeight:700, color:theme.text }}>{isNew?"Novo grupo":"Editar grupo"}</h3>
          <button onClick={onClose} style={{ background:theme.bgInput, border:"none", borderRadius:8, width:30, height:30, color:theme.textMuted, fontSize:17, cursor:"pointer" }}>×</button>
        </div>
        <div style={{ marginBottom:16 }}>
          <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:6, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Nome</label>
          <input value={label} onChange={e=>setLabel(e.target.value)} style={inp} placeholder="Ex: Novos Leads..."
            onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}
            onKeyDown={e=>e.key==="Enter"&&salvar()} autoFocus/>
        </div>
        <div style={{ marginBottom:20 }}>
          <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:8, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Cor</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {CORES.map(c=>(
              <div key={c} onClick={()=>setColor(c)} style={{ width:28, height:28, borderRadius:8, background:c, cursor:"pointer", border:color===c?`3px solid ${theme.text}`:"3px solid transparent", boxShadow:color===c?`0 0 0 2px ${c}66`:"none" }}/>
            ))}
          </div>
        </div>
        <div style={{ background:color+"15", border:`2px solid ${color}`, borderRadius:10, padding:"8px 14px", marginBottom:20, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontWeight:700, color, fontSize:14 }}>{label||"Pré-visualização"}</span>
          <span style={{ background:color+"33", color, borderRadius:99, fontSize:11, fontWeight:700, padding:"1px 8px", marginLeft:"auto" }}>0</span>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={salvar} disabled={load||!label.trim()} style={{ flex:1, background:`linear-gradient(135deg,${color},${color}CC)`, border:"none", borderRadius:10, padding:"11px 0", color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer", opacity:!label.trim()?.5:1 }}>
            {load?"Salvando...":"Salvar"}
          </button>
          {!isNew&&<button onClick={()=>{ onDelete(grupo._id); onClose(); }} style={{ background:"#ef444418", border:"1px solid #ef444433", borderRadius:10, padding:"11px 14px", color:"#ef4444", fontWeight:700, fontSize:13, cursor:"pointer" }}>Excluir</button>}
        </div>
      </div>
    </div>
  );
}

// ── MODAL LEAD COMPLETO ──────────────────────────────────
function ModalLead({ lead, grupos, onClose, onSave, onDelete }) {
  const { theme } = useTheme();
  const isNew = !lead?._id;
  const [form, setForm] = useState({
    nome:lead?.nome||"", empresa:lead?.empresa||"",
    telefone:lead?.telefone||"", email:lead?.email||"",
    cargo:lead?.cargo||"", origem:lead?.origem||"Instagram",
    statusLead:lead?.statusLead||"novo",
    grupoLeadId:lead?.grupoLeadId||"", grupoLeadLabel:lead?.grupoLeadLabel||"",
    comentarios:lead?.comentarios||"", obs:lead?.obs||"",
    ultimaInteracao:lead?.ultimaInteracao?new Date(lead.ultimaInteracao).toISOString().slice(0,10):"",
  });
  const [nota, setNota] = useState("");
  const [load, setLoad] = useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const inp={ width:"100%", boxSizing:"border-box", background:theme.bgInput, border:`1.5px solid ${theme.border}`, borderRadius:10, padding:"9px 12px", color:theme.text, fontSize:13, outline:"none", colorScheme:theme.name, fontFamily:"'Inter',sans-serif", transition:"border-color .15s" };
  const salvar=async()=>{
    if(!form.nome.trim()) return alert("Nome obrigatório");
    setLoad(true);
    try{ await onSave({...lead,...form,stage:lead?.stage||"prospeccao"}); }
    catch(e){ alert(e.response?.data?.erro||"Erro"); }finally{ setLoad(false); }
  };
  const addNota=async()=>{
    if(!nota.trim()||!lead?._id) return;
    await api.post(`/leads/${lead._id}/atividades`,{tipo:"nota",descricao:nota});
    setNota(""); onClose();
  };
  const waLink=form.telefone?`https://wa.me/55${form.telefone.replace(/\D/g,"")}`:null;

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.75)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:20, padding:30, width:"100%", maxWidth:560, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 80px rgba(0,0,0,.6)" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
          <div>
            <div style={{ fontSize:11, color:ORANGE, fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>LEAD</div>
            <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:theme.text }}>{form.nome||"Novo Lead"}</h2>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {waLink&&<a href={waLink} target="_blank" rel="noreferrer" style={{ background:"#25d36622", border:"1px solid #25d36644", borderRadius:8, padding:"6px 12px", color:"#25d366", fontSize:12, fontWeight:700, textDecoration:"none" }}>💬 WhatsApp</a>}
            <button onClick={onClose} style={{ background:theme.bgInput, border:"none", borderRadius:8, width:32, height:32, color:theme.textMuted, fontSize:18, cursor:"pointer" }}>×</button>
          </div>
        </div>

        {/* Status */}
        <div style={{ marginBottom:16 }}>
          <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:8, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Status</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {STATUS_LEAD.map(s=>(
              <button key={s.id} onClick={()=>set("statusLead",s.id)} style={{ padding:"5px 12px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", border:`2px solid ${form.statusLead===s.id?s.color:theme.border}`, background:form.statusLead===s.id?s.color+"22":"transparent", color:form.statusLead===s.id?s.color:theme.textMuted }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grupo */}
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Grupo</label>
          <select value={form.grupoLeadId||""} onChange={e=>{ const g=grupos.find(x=>x._id===e.target.value); set("grupoLeadId",e.target.value); set("grupoLeadLabel",g?.label||""); }} style={{ ...inp, appearance:"auto" }}
            onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}>
            <option value="">Sem grupo</option>
            {grupos.map(g=><option key={g._id} value={g._id}>{g.label}</option>)}
          </select>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          {[["Nome *","nome"],["Empresa","empresa"],["Cargo","cargo"],["Email","email"],["Telefone","telefone"]].map(([label,key])=>(
            <div key={key}>
              <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>{label}</label>
              <input value={form[key]||""} onChange={e=>set(key,e.target.value)} style={inp}
                onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            </div>
          ))}
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Fonte do lead</label>
            <select value={form.origem||"Instagram"} onChange={e=>set("origem",e.target.value)} style={{ ...inp, appearance:"auto" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}>
              {ORIGENS.map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Última interação</label>
            <input type="date" value={form.ultimaInteracao||""} onChange={e=>set("ultimaInteracao",e.target.value)} style={inp}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Comentários</label>
            <input value={form.comentarios||""} onChange={e=>set("comentarios",e.target.value)} style={inp}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Observações</label>
          <textarea value={form.obs||""} onChange={e=>set("obs",e.target.value)} rows={2} style={{ ...inp, resize:"vertical" }}
            onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
        </div>

        {!isNew&&lead?.atividades?.length>0&&(
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:8, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Histórico</label>
            {[...lead.atividades].reverse().slice(0,3).map((a,i)=>(
              <div key={i} style={{ background:theme.bgInput, borderRadius:8, padding:"8px 12px", marginBottom:6 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                  <span style={{ color:ORANGE, fontWeight:600, fontSize:12 }}>{a.nomeUsuario}</span>
                  <span style={{ fontSize:11, color:theme.textMuted }}>{new Date(a.createdAt).toLocaleDateString("pt-BR")}</span>
                </div>
                <div style={{ fontSize:13, color:theme.textSub }}>{a.descricao}</div>
              </div>
            ))}
          </div>
        )}

        {!isNew&&(
          <div style={{ display:"flex", gap:8, marginBottom:16 }}>
            <input value={nota} onChange={e=>setNota(e.target.value)} placeholder="Adicionar nota..." style={{ ...inp, flex:1 }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            <button onClick={addNota} style={{ background:ORANGE, border:"none", borderRadius:10, padding:"9px 14px", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700 }}>+</button>
          </div>
        )}

        <div style={{ display:"flex", gap:10 }}>
          <button onClick={salvar} disabled={load} style={{ flex:1, background:`linear-gradient(135deg,${ORANGE},#FF9A5C)`, border:"none", borderRadius:12, padding:"13px 0", color:"#fff", fontWeight:700, fontSize:15, cursor:"pointer", opacity:load?.7:1, boxShadow:`0 4px 14px ${ORANGE}44` }}>
            {load?"Salvando...":"Salvar"}
          </button>
          {!isNew&&<button onClick={()=>onDelete(lead._id)} style={{ background:"#ef444418", border:"1px solid #ef444433", borderRadius:12, padding:"13px 18px", color:"#ef4444", fontWeight:600, fontSize:14, cursor:"pointer" }}>Excluir</button>}
        </div>
      </div>
    </div>
  );
}

// ── COMPONENTE PRINCIPAL ─────────────────────────────────
export default function Leads({ currentUser }) {
  const { theme } = useTheme();
  const [leads,    setLeads]   = useState([]);
  const [grupos,   setGrupos]  = useState([]);
  const [loading,  setLoading] = useState(true);
  const [search,   setSearch]  = useState("");
  const [modal,    setModal]   = useState(null);
  const [modalGrupo, setModalGrupo] = useState(null);
  const [colapsados, setColapsados] = useState({});
  const [selected, setSelected] = useState([]);
  const [dragLeadId,    setDragLeadId]    = useState(null);
  const [dragOverGrupo, setDragOverGrupo] = useState(null);
  const [menuMover, setMenuMover] = useState(null);
  // Linha nova por grupo — null ou grupoId
  const [novaLinhaGrupo, setNovaLinhaGrupo] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const [leadsRes, gruposRes] = await Promise.all([
        api.get("/leads?limit=1000"),
        api.get("/grupos-lead"),
      ]);
      setLeads(leadsRes.data.leads.filter(l=>!l.isProjeto));
      setGrupos(gruposRes.data.grupos);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(()=>{ carregar(); },[carregar]);

  const salvarGrupo=async(form)=>{
    if(form._id){ const{data}=await api.put(`/grupos-lead/${form._id}`,form); setGrupos(prev=>prev.map(g=>g._id===data._id?data:g)); }
    else{ const{data}=await api.post("/grupos-lead",form); setGrupos(prev=>[...prev,data]); }
  };
  const excluirGrupo=async(id)=>{
    await api.delete(`/grupos-lead/${id}`);
    setGrupos(prev=>prev.filter(g=>g._id!==id));
    setLeads(prev=>prev.map(l=>String(l.grupoLeadId)===String(id)?{...l,grupoLeadId:null,grupoLeadLabel:null}:l));
  };

  // Salvar lead novo (linha monday ou modal)
  const criarLead=async(form)=>{
    const{data}=await api.post("/leads",{ ...form, stage:"prospeccao", origem:form.origem||"Instagram", statusLead:form.statusLead||"novo" });
    setLeads(prev=>[data,...prev]);
    setNovaLinhaGrupo(null);
    setModal(null);
  };

  const salvarLead=async(form)=>{
    if(form._id){ const{data}=await api.put(`/leads/${form._id}`,form); setLeads(prev=>prev.map(l=>l._id===data._id?data:l)); }
    else{ await criarLead(form); return; }
    setModal(null);
  };
  const excluirLead=async(id)=>{ await api.delete(`/leads/${id}`); setLeads(prev=>prev.filter(l=>l._id!==id)); setModal(null); };

  const atualizarCampo=async(id,campo,valor)=>{
    try{ const{data}=await api.put(`/leads/${id}`,{[campo]:valor}); setLeads(prev=>prev.map(l=>l._id===id?{...l,...data}:l)); }catch{}
  };
  const atualizarStatus=async(id,statusLead)=>{
    try{ await api.put(`/leads/${id}`,{statusLead}); setLeads(prev=>prev.map(l=>l._id===id?{...l,statusLead}:l)); }catch{}
  };
  const moverParaGrupo=async(leadId,grupoId,grupoLabel)=>{
    try{
      await api.put(`/leads/${leadId}`,{grupoLeadId:grupoId||null,grupoLeadLabel:grupoLabel||null});
      setLeads(prev=>prev.map(l=>l._id===leadId?{...l,grupoLeadId:grupoId||null,grupoLeadLabel:grupoLabel||null}:l));
    }catch{}
  };
  const moverSelecionados=async(grupoId,grupoLabel)=>{
    await Promise.all(selected.map(id=>moverParaGrupo(id,grupoId,grupoLabel)));
    setSelected([]);
  };

  const filtered=leads.filter(l=>{ const q=search.toLowerCase(); return !q||l.nome?.toLowerCase().includes(q)||l.empresa?.toLowerCase().includes(q)||l.email?.toLowerCase().includes(q); });
  const leadsSemGrupo = filtered.filter(l=>!l.grupoLeadId);
  const leadsPorGrupo = (id) => filtered.filter(l=>String(l.grupoLeadId)===String(id));
  const todosGrupos   = [{ _id:"__sem__", label:"Sem grupo", color:"#8E8E93", _virtual:true }, ...grupos];

  const TH=({children,w,center})=>(
    <th style={{ padding:"10px 14px", fontSize:11, color:theme.textMuted, fontWeight:600, letterSpacing:".05em", textTransform:"uppercase", textAlign:center?"center":"left", borderRight:`1px solid ${theme.border}`, width:w, minWidth:w, background:theme.bgInput, whiteSpace:"nowrap" }}>{children}</th>
  );
  const TD=({children,w,center,style:s})=>(
    <td style={{ padding:"0 10px", height:44, borderRight:`1px solid ${theme.border}`, borderBottom:`1px solid ${theme.border}`, width:w, minWidth:w, textAlign:center?"center":"left", verticalAlign:"middle", ...s }}>{children}</td>
  );

  return (
    <div style={{ fontFamily:"'Inter',-apple-system,sans-serif", background:theme.bg, minHeight:"100vh" }}
      onClick={()=>setMenuMover(null)}>

      {/* Header */}
      <div style={{ padding:"20px 28px 0", background:theme.bgSecond, borderBottom:`1px solid ${theme.border}` }}>

        {/* Banner mover selecionados */}
        {selected.length>0&&(
          <div style={{ background:ORANGE+"18", border:`1px solid ${ORANGE}44`, borderRadius:10, padding:"10px 16px", marginBottom:12, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <span style={{ fontSize:13, fontWeight:600, color:ORANGE }}>{selected.length} selecionado{selected.length>1?"s":""}</span>
            <span style={{ fontSize:13, color:theme.textMuted }}>→ Mover para:</span>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              <button onClick={()=>moverSelecionados(null,null)} style={{ background:"#8E8E9322", border:"1px solid #8E8E9344", borderRadius:8, padding:"4px 12px", color:"#8E8E93", fontSize:12, fontWeight:600, cursor:"pointer" }}>Sem grupo</button>
              {grupos.map(g=>(
                <button key={g._id} onClick={()=>moverSelecionados(g._id,g.label)} style={{ background:g.color+"22", border:`1px solid ${g.color}44`, borderRadius:8, padding:"4px 12px", color:g.color, fontSize:12, fontWeight:600, cursor:"pointer" }}>{g.label}</button>
              ))}
            </div>
            <button onClick={()=>setSelected([])} style={{ marginLeft:"auto", background:"none", border:"none", color:theme.textMuted, fontSize:12, cursor:"pointer", textDecoration:"underline" }}>Cancelar</button>
          </div>
        )}

        <div style={{ display:"flex", gap:20, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ display:"flex", gap:20 }}>
            {[["Total",leads.length,theme.text],["Novos",leads.filter(l=>!l.statusLead||l.statusLead==="novo").length,"#6366f1"],["Convertidos",leads.filter(l=>l.statusLead==="convertido").length,"#10b981"]].map(([label,val,color])=>(
              <div key={label}><div style={{ fontSize:11, color:theme.textMuted, fontWeight:500 }}>{label}</div><div style={{ fontSize:20, fontWeight:800, color }}>{val}</div></div>
            ))}
          </div>
          <div style={{ marginLeft:"auto", display:"flex", gap:10, alignItems:"center" }}>
            <input placeholder="🔍 Pesquisar lead..." value={search} onChange={e=>setSearch(e.target.value)}
              style={{ background:theme.bgCard, border:`1.5px solid ${theme.border}`, borderRadius:10, padding:"8px 14px", color:theme.text, fontSize:13, outline:"none", colorScheme:theme.name, width:220, fontFamily:"'Inter',sans-serif" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            <button onClick={()=>setModalGrupo({})} style={{ background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:10, padding:"8px 16px", color:theme.textSub, fontWeight:600, fontSize:13, cursor:"pointer" }}>+ Novo grupo</button>
            {/* BOTÃO CRIAR LEAD — abre linha no primeiro grupo */}
            <button onClick={()=>setNovaLinhaGrupo(todosGrupos[0]._id)} style={{ background:`linear-gradient(135deg,${ORANGE},#FF9A5C)`, border:"none", borderRadius:10, padding:"9px 18px", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", boxShadow:`0 4px 12px ${ORANGE}44`, display:"flex", alignItems:"center", gap:6 }}>
              + Criar lead
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div style={{ overflowX:"auto", padding:"16px 0 40px" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", minWidth:1100 }}>
          <thead>
            <tr>
              <TH w={36}><input type="checkbox" onChange={e=>setSelected(e.target.checked?leads.map(l=>l._id):[])}/></TH>
              <TH w={240}>Lead</TH>
              <TH w={190} center>Status</TH>
              <TH w={120}>Cargo</TH>
              <TH w={170}>E-mail</TH>
              <TH w={130}>Telefone</TH>
              <TH w={130}>Fonte do lead</TH>
              <TH w={110} center>Última interação</TH>
              <TH w={200}>Comentários</TH>
              <TH w={52} center>⋮</TH>
            </tr>
          </thead>

          {loading?(
            <tbody><tr><td colSpan={10} style={{ padding:60, textAlign:"center", color:theme.textMuted }}>Carregando...</td></tr></tbody>
          ):(
            todosGrupos.map(grupo=>{
              const grupoLeads = grupo._id==="__sem__"?leadsSemGrupo:leadsPorGrupo(grupo._id);
              const colapsado  = colapsados[grupo._id];
              const isDragOver = dragOverGrupo===grupo._id;

              return (
                <tbody key={grupo._id}
                  onDragOver={e=>{ e.preventDefault(); setDragOverGrupo(grupo._id); }}
                  onDragLeave={()=>setDragOverGrupo(null)}
                  onDrop={e=>{ e.preventDefault(); const id=e.dataTransfer.getData("leadId"); if(id) moverParaGrupo(id,grupo._id==="__sem__"?null:grupo._id,grupo._id==="__sem__"?null:grupo.label); setDragOverGrupo(null); setDragLeadId(null); }}>

                  {/* Header grupo */}
                  <tr>
                    <td colSpan={10} style={{ padding:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderBottom:`3px solid ${grupo.color}`, background:isDragOver?grupo.color+"25":grupo.color+"08", userSelect:"none", transition:"background .15s" }}>
                        <span onClick={()=>setColapsados(c=>({...c,[grupo._id]:!c[grupo._id]}))} style={{ color:grupo.color, fontSize:14, cursor:"pointer", display:"inline-block", transform:colapsado?"rotate(-90deg)":"rotate(0deg)", transition:"transform .2s" }}>▾</span>
                        <span style={{ fontWeight:700, color:grupo.color, fontSize:14 }}>{grupo.label}</span>
                        <span style={{ background:grupo.color+"25", color:grupo.color, borderRadius:99, fontSize:11, fontWeight:700, padding:"2px 10px" }}>{grupoLeads.length}</span>
                        {isDragOver&&<span style={{ fontSize:12, color:grupo.color, fontWeight:600, background:grupo.color+"22", borderRadius:6, padding:"2px 10px" }}>↓ Solte aqui</span>}
                        {!grupo._virtual&&(
                          <button onClick={()=>setModalGrupo(grupo)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:12, padding:"2px 8px", borderRadius:6 }}
                            onMouseEnter={e=>{ e.currentTarget.style.background=grupo.color+"18"; e.currentTarget.style.color=grupo.color; }}
                            onMouseLeave={e=>{ e.currentTarget.style.background="none"; e.currentTarget.style.color=theme.textMuted; }}>
                            ✏️ Editar
                          </button>
                        )}
                        {/* Botão criar lead dentro do grupo */}
                        <button onClick={()=>setNovaLinhaGrupo(grupo._id)} style={{ background:"none", border:`1px solid ${grupo.color}44`, color:grupo.color, borderRadius:8, padding:"3px 10px", fontSize:11, fontWeight:600, cursor:"pointer", marginLeft:4 }}
                          onMouseEnter={e=>{ e.currentTarget.style.background=grupo.color+"18"; }}
                          onMouseLeave={e=>{ e.currentTarget.style.background="none"; }}>
                          + Criar lead aqui
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Linha nova ESTILO MONDAY — aparece no topo do grupo */}
                  {!colapsado && novaLinhaGrupo===grupo._id && (
                    <LinhaNovaMonday
                      grupoId={grupo._id==="__sem__"?null:grupo._id}
                      grupoLabel={grupo._id==="__sem__"?null:grupo.label}
                      grupos={grupos}
                      onSave={criarLead}
                      onCancel={()=>setNovaLinhaGrupo(null)}
                    />
                  )}

                  {/* Linhas dos leads */}
                  {!colapsado&&grupoLeads.map(lead=>{
                    const isSel  = selected.includes(lead._id);
                    const isDrag = dragLeadId===lead._id;
                    const waLink = lead.telefone?`https://wa.me/55${lead.telefone.replace(/\D/g,"")}`:null;
                    return (
                      <tr key={lead._id} draggable
                        onDragStart={e=>{ e.dataTransfer.setData("leadId",lead._id); setDragLeadId(lead._id); }}
                        onDragEnd={()=>setDragLeadId(null)}
                        style={{ background:isSel?ORANGE+"08":isDrag?"rgba(255,107,43,.05)":theme.bgCard, transition:"background .1s", opacity:isDrag?.6:1 }}
                        onMouseEnter={e=>{ if(!isSel&&!isDrag) e.currentTarget.style.background=theme.bgHover; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background=isSel?ORANGE+"08":isDrag?"rgba(255,107,43,.05)":theme.bgCard; }}>

                        <TD w={36} center>
                          <input type="checkbox" checked={isSel} onChange={()=>setSelected(prev=>prev.includes(lead._id)?prev.filter(x=>x!==lead._id):[...prev,lead._id])} onClick={e=>e.stopPropagation()}/>
                        </TD>
                        <TD w={240}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <span style={{ color:theme.border, fontSize:14, cursor:"grab", flexShrink:0 }}>⠿</span>
                            <div style={{ flex:1, overflow:"hidden" }}>
                              <IE value={lead.nome} placeholder="Nome" onChange={v=>atualizarCampo(lead._id,"nome",v)} style={{ fontWeight:600 }}/>
                              {lead.empresa&&<div style={{ fontSize:11, color:theme.textMuted, paddingLeft:4 }}>{lead.empresa}</div>}
                            </div>
                            <button onClick={()=>setModal(lead)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:13, opacity:0, padding:"2px 4px", borderRadius:4, flexShrink:0 }}
                              onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0}>✏️</button>
                          </div>
                        </TD>
                        <TD w={190} center>
                          <StatusBadge statusLead={lead.statusLead||"novo"} onChange={v=>atualizarStatus(lead._id,v)}/>
                        </TD>
                        <TD w={120}><IE value={lead.cargo} placeholder="Cargo" onChange={v=>atualizarCampo(lead._id,"cargo",v)}/></TD>
                        <TD w={170}><IE value={lead.email} placeholder="email@..." onChange={v=>atualizarCampo(lead._id,"email",v)} style={{ color:"#3b82f6", fontSize:12 }}/></TD>
                        <TD w={130}>
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            {lead.telefone&&<span style={{ fontSize:13, flexShrink:0 }}>🇧🇷</span>}
                            <IE value={lead.telefone} placeholder="Telefone" onChange={v=>atualizarCampo(lead._id,"telefone",v)} style={{ color:"#3b82f6", fontSize:12 }}/>
                            {waLink&&<a href={waLink} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{ color:"#25d366", fontSize:14, flexShrink:0, textDecoration:"none" }}>💬</a>}
                          </div>
                        </TD>
                        <TD w={130}>
                          <span style={{ fontSize:12, background:theme.bgInput, borderRadius:6, padding:"2px 10px", color:theme.textSub }}>{lead.origem||"—"}</span>
                        </TD>
                        <TD w={110} center>
                          <span style={{ fontSize:12, color:theme.textMuted }}>{lead.ultimaInteracao?fmtData(lead.ultimaInteracao):"—"}</span>
                        </TD>
                        <TD w={200}>
                          <IE value={lead.comentarios} placeholder="Comentário..." onChange={v=>atualizarCampo(lead._id,"comentarios",v)} style={{ fontSize:12, color:theme.textSub }}/>
                        </TD>
                        <TD w={52} center>
                          <button onClick={e=>{ e.stopPropagation(); setMenuMover({lead,x:e.clientX,y:e.clientY}); }}
                            style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:20, borderRadius:6, padding:"2px 8px", width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center" }}
                            onMouseEnter={e=>{ e.currentTarget.style.background=theme.bgInput; e.currentTarget.style.color=theme.text; }}
                            onMouseLeave={e=>{ e.currentTarget.style.background="none"; e.currentTarget.style.color=theme.textMuted; }}>⋮</button>
                        </TD>
                      </tr>
                    );
                  })}

                  {/* Botão + Adicionar lead */}
                  {!colapsado&&(
                    <tr>
                      <td colSpan={10} style={{ padding:"6px 14px", borderBottom:`1px solid ${theme.border}`, background:theme.bg }}>
                        <button onClick={()=>setNovaLinhaGrupo(grupo._id)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", gap:6, padding:"5px 8px", borderRadius:8, fontFamily:"'Inter',sans-serif" }}
                          onMouseEnter={e=>{ e.currentTarget.style.background=grupo.color+"10"; e.currentTarget.style.color=grupo.color; }}
                          onMouseLeave={e=>{ e.currentTarget.style.background="none"; e.currentTarget.style.color=theme.textMuted; }}>
                          + Adicionar lead
                        </button>
                      </td>
                    </tr>
                  )}

                  {!colapsado&&grupoLeads.length>0&&(
                    <tr style={{ background:grupo.color+"06" }}>
                      <td colSpan={9} style={{ padding:"8px 14px", fontSize:12, color:theme.textMuted, borderBottom:`1px solid ${theme.border}`, fontWeight:500 }}>
                        {grupoLeads.length} lead{grupoLeads.length!==1?"s":""}
                      </td>
                      <td style={{ borderBottom:`1px solid ${theme.border}` }}/>
                    </tr>
                  )}

                  <tr><td colSpan={10} style={{ height:16, background:theme.bg }}/></tr>
                </tbody>
              );
            })
          )}
        </table>

        <div style={{ padding:"8px 28px" }}>
          <button onClick={()=>setModalGrupo({})} style={{ background:"none", border:`1.5px dashed ${theme.border}`, borderRadius:10, padding:"10px 20px", color:theme.textMuted, cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", gap:8, fontFamily:"'Inter',sans-serif", width:"100%" }}
            onMouseEnter={e=>{ e.currentTarget.style.borderColor=ORANGE; e.currentTarget.style.color=ORANGE; }}
            onMouseLeave={e=>{ e.currentTarget.style.borderColor=theme.border; e.currentTarget.style.color=theme.textMuted; }}>
            + Adicionar novo grupo
          </button>
        </div>
      </div>

      {/* Menu ⋮ */}
      {menuMover&&(
        <div style={{ position:"fixed", top:menuMover.y, left:menuMover.x, zIndex:3000 }} onClick={e=>e.stopPropagation()}>
          <div style={{ background:"#2C2C2F", border:"1px solid #3D3D42", borderRadius:14, padding:8, minWidth:220, boxShadow:"0 16px 48px rgba(0,0,0,.6)" }}>
            <div style={{ fontSize:11, color:"#8E8E93", fontWeight:600, letterSpacing:".05em", textTransform:"uppercase", padding:"6px 10px 4px" }}>Mover para grupo</div>
            <div onClick={()=>{ moverParaGrupo(menuMover.lead._id,null,null); setMenuMover(null); }}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:8, cursor:"pointer" }}
              onMouseEnter={e=>e.currentTarget.style.background="#8E8E9318"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{ width:10, height:10, borderRadius:3, background:"#8E8E93" }}/><span style={{ fontSize:13, color:"#F2F2F7" }}>Sem grupo</span>
            </div>
            {grupos.map(g=>(
              <div key={g._id} onClick={()=>{ moverParaGrupo(menuMover.lead._id,g._id,g.label); setMenuMover(null); }}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:8, cursor:"pointer" }}
                onMouseEnter={e=>e.currentTarget.style.background=g.color+"18"}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{ width:10, height:10, borderRadius:3, background:g.color }}/><span style={{ fontSize:13, color:"#F2F2F7" }}>{g.label}</span>
                {String(menuMover.lead.grupoLeadId)===String(g._id)&&<span style={{ marginLeft:"auto", fontSize:11, color:g.color }}>atual</span>}
              </div>
            ))}
            <div style={{ borderTop:"1px solid #3D3D42", margin:"6px 0" }}/>
            <div onClick={()=>{ setModal(menuMover.lead); setMenuMover(null); }}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:8, cursor:"pointer" }}
              onMouseEnter={e=>e.currentTarget.style.background="#ffffff10"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <span style={{ fontSize:13, color:"#F2F2F7" }}>✏️ Editar lead completo</span>
            </div>
          </div>
        </div>
      )}

      {modal!==null&&<ModalLead lead={Object.keys(modal).length?modal:null} grupos={grupos} onClose={()=>setModal(null)} onSave={salvarLead} onDelete={excluirLead}/>}
      {modalGrupo!==null&&<ModalGrupo grupo={Object.keys(modalGrupo).length?modalGrupo:null} onClose={()=>setModalGrupo(null)} onSave={salvarGrupo} onDelete={excluirGrupo}/>}
    </div>
  );
}
